import React, { FC } from 'react';
import { Box, Button, DialogActions, DialogContent, Typography } from '@mui/material';
import { useMutation } from '@apollo/client';
import { linkUserToOrganizationTemplate } from 'apollo/templates/user/mutation.graphql';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { User } from 'models/user';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { LoadingButton } from '@mui/lab';
import { Organization } from 'models/organization';

export type LinkedUser = {
    organization?: string;
    role: string;
    active: boolean;
    type: string;
    isWhiteLabeled: boolean;
};

type LinkUserInfoComponentProps = {
    user: User;
    organization: string;
    userType: string;
    role: string;
    onBack: () => void;
    onUserLinked: (user: User) => void;
};
const LinkUserInfo: FC<LinkUserInfoComponentProps> = ({
    user,
    userType,
    organization,
    role,
    onBack,
    onUserLinked,
}: LinkUserInfoComponentProps) => {
    const { t: translate } = useTranslation(['common', 'users', 'organization']);
    const { showBoundary } = useDenthubErrorBoundary();

    const [linkUserToOrganization, { loading }] = useMutation(linkUserToOrganizationTemplate, {
        onCompleted: ({ linkUserToOrganization }) => {
            onUserLinked(linkUserToOrganization);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const formik = useFormik({
        initialValues: {
            organization: organization,
            role,
            type: userType === 'DENTIST' ? 'dentist' : 'lab_technician',
            active: true,
            isWhiteLabeled: (user.organization as Organization)?.isWhiteLabeled || false,
        },
        validateOnMount: true,
        enableReinitialize: true,
        onSubmit: async (values: LinkedUser) => {
            await linkUserToOrganization({
                variables: {
                    userId: user._id,
                    linkDetails: values,
                },
            });
        },
    });

    return (
        <form onSubmit={formik.handleSubmit}>
            <DialogContent>
                <Box mb={2}>
                    <Typography variant="body1">{translate('users:LINK_USER_INFO')}</Typography>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onBack}>{translate('common:BACK')}</Button>
                <LoadingButton loading={loading} disabled={!formik.isValid} variant="contained" type="submit">
                    {translate('common:LINK')}
                </LoadingButton>
            </DialogActions>
        </form>
    );
};

export default LinkUserInfo;
